import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import BASE_URL from "../../../../config/config";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiX, FiEdit2, FiTrash2, FiCpu, FiHardDrive } from "react-icons/fi";
import Loader from "../../../../components/Loader";
import Popup from "../../../../components/Popup";

const TIPO_ESTADO = ["Activo", "En reparación", "No activo"];

const claseEstado = (estado) => {
  if (estado === "Activo") return "mat-badge-estado-activo";
  if (estado === "En reparación") return "mat-badge-estado-reparacion";
  return "mat-badge-estado-inactivo";
};

const FORM_VACIO = {
  marca: "",
  modelo: "",
  numero_serie: "",
  cpu: "",
  ram: "",
  almacenamiento: "",
  sistemaOperativo: "",
  estado: "Activo",
  personaAsignada: "",
  ubicacion: "",
  fechaCompra: "",
  proveedor: "",
  notas: "",
};

const EquiposManager = () => {
  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};
  const isAdmin = tokenInfo?.role === "admin";
  const navigate = useNavigate();

  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [editId, setEditId] = useState(null);

  const [borrarId, setBorrarId] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: token || "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const fetchEquipos = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}/equipos/`, { headers });
      setEquipos(data || []);
    } catch (err) {
      console.error("Error al obtener equipos:", err);
      toast.error("Error al obtener la lista de equipos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipos();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const q = searchText.toLowerCase();
    return equipos.filter((e) =>
      [
        e.marca,
        e.modelo,
        e.numero_serie,
        e.cpu,
        e.almacenamiento,
        e.sistemaOperativo,
        e.personaAsignada,
        e.ubicacion,
        e.proveedor,
        e.estado,
        e.notas,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [equipos, searchText]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setForm(FORM_VACIO);
    setEditId(null);
  };

  const cerrarPanel = () => {
    setMostrarForm(false);
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/equipos/${editId}`, form, { headers });
        toast.success("Equipo actualizado correctamente.");
      } else {
        await axios.post(`${BASE_URL}/equipos/`, form, { headers });
        toast.success("Equipo creado correctamente.");
      }
      await fetchEquipos();
      cerrarPanel();
    } catch (err) {
      console.error("Error guardando equipo:", err);
      toast.error("Error guardando equipo.");
    }
  };

  const onEdit = (item) => {
    setEditId(item.id);
    setForm({
      marca: item.marca ?? "",
      modelo: item.modelo ?? "",
      numero_serie: item.numero_serie ?? "",
      cpu: item.cpu ?? "",
      ram: item.ram ?? "",
      almacenamiento: item.almacenamiento ?? "",
      sistemaOperativo: item.sistemaOperativo ?? "",
      estado: item.estado ?? "Activo",
      personaAsignada: item.personaAsignada ?? "",
      ubicacion: item.ubicacion ?? "",
      fechaCompra: item.fechaCompra ?? "",
      proveedor: item.proveedor ?? "",
      notas: item.notas ?? "",
    });
    setMostrarForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pedirBorrado = (id) => {
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }
    setBorrarId(id);
  };

  const confirmarBorrado = async () => {
    const id = borrarId;
    setBorrarId(null);
    try {
      await axios.delete(`${BASE_URL}/equipos/${id}`, { headers });
      toast.success("Equipo eliminado correctamente.");
      await fetchEquipos();
    } catch (err) {
      console.error("Error al eliminar equipo:", err);
      toast.error("Error al eliminar equipo.");
    }
  };

  if (!token) {
    return (
      <div className="site-width">
        <p>Inicia sesión para continuar.</p>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" />
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← Volver
      </button>
      <div className="dashboard-content gap-equipos">
        <div className="pagina-cabecera">
          <h2>Gestión de equipos</h2>
          <p>Añade, edita, elimina y consulta equipos informáticos y sus periféricos asociados.</p>
        </div>

        {/* Barra de herramientas */}
        <div className="mat-toolbar">
          <input
            type="text"
            placeholder="Buscar por marca, modelo, nº serie, usuario, ubicación…"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
          <div className="mat-toolbar-acciones">
            <select
              className="selector-page-size"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={15}>15 / página</option>
              <option value={30}>30 / página</option>
              <option value={60}>60 / página</option>
            </select>
            <button
              type="button"
              className={`chip-toggle ${mostrarForm ? 'activo' : ''}`}
              onClick={() => (mostrarForm ? cerrarPanel() : setMostrarForm(true))}
            >
              <FiPlus /> Nuevo equipo
            </button>
          </div>
        </div>

        {/* Panel plegable: crear/editar equipo */}
        {mostrarForm && (
          <form className="mat-form-panel" onSubmit={onSubmit}>
            <div className="mat-form-panel-header">
              <h3>{editId ? `Editar equipo #${editId}` : "Nuevo equipo"}</h3>
              <button type="button" className="mat-close-btn" onClick={cerrarPanel} aria-label="Cerrar">
                <FiX />
              </button>
            </div>

            <div className="mat-form-grid">
              <div className="mat-campo">
                <label>Marca</label>
                <input type="text" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Modelo</label>
                <input type="text" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Nº Serie</label>
                <input type="text" value={form.numero_serie} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>CPU</label>
                <input type="text" value={form.cpu} onChange={(e) => setForm({ ...form, cpu: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>RAM (GB)</label>
                <input type="number" min="0" value={form.ram} onChange={(e) => setForm({ ...form, ram: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Almacenamiento</label>
                <input type="text" value={form.almacenamiento} onChange={(e) => setForm({ ...form, almacenamiento: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Sistema operativo</label>
                <input type="text" value={form.sistemaOperativo} onChange={(e) => setForm({ ...form, sistemaOperativo: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Estado</label>
                <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} required>
                  {form.estado && !TIPO_ESTADO.includes(form.estado) && (
                    <option value={form.estado}>{form.estado} (actual)</option>
                  )}
                  {TIPO_ESTADO.map((estado) => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>

              <div className="mat-campo">
                <label>Fecha de compra</label>
                <input type="date" value={form.fechaCompra} onChange={(e) => setForm({ ...form, fechaCompra: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Persona asignada</label>
                <input type="text" value={form.personaAsignada} onChange={(e) => setForm({ ...form, personaAsignada: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Ubicación</label>
                <input type="text" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>Proveedor</label>
                <input type="text" value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} />
              </div>

              <div className="mat-campo mat-campo-full">
                <label>Notas</label>
                <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
            </div>

            <div className="mat-form-actions">
              <button type="button" className="mat-btn-secundario" onClick={resetForm}>Limpiar</button>
              <button type="submit" className="mat-btn-primario">
                {editId ? "Actualizar equipo" : "Añadir equipo"}
              </button>
            </div>
          </form>
        )}

        {/* Listado */}
        {loading ? (
          <div className="loader-fullscreen"><Loader /></div>
        ) : (
          <div className="overflow-tabla">
            <div className="mat-panel">
              <div className="mat-list-header mat-grid-equipos">
                <span>Marca / Modelo</span>
                <span>Nº Serie</span>
                <span>Especificaciones</span>
                <span>Usuario</span>
                <span>Ubicación</span>
                <span>Estado</span>
                <span>Acciones</span>
              </div>

              <div className="mat-rows">
                {pageItems.length === 0 ? (
                  <div className="mat-vacio">No hay equipos que coincidan con la búsqueda.</div>
                ) : (
                  pageItems.map((e) => (
                    <div key={e.id} className="mat-row mat-grid-equipos">
                      <div className="mat-col">
                        <span className="mat-col-titulo">{e.marca} {e.modelo}</span>
                      </div>
                      <div className="mat-col">
                        <code className="mat-serie">{e.numero_serie || '—'}</code>
                      </div>
                      <div className="mat-col mat-col-specs">
                        {e.cpu && <span><FiCpu /> {e.cpu}</span>}
                        {(e.ram || e.almacenamiento) && (
                          <span><FiHardDrive /> {[e.ram && `${e.ram}GB RAM`, e.almacenamiento].filter(Boolean).join(' · ')}</span>
                        )}
                        {e.sistemaOperativo && <span className="mat-col-so">{e.sistemaOperativo}</span>}
                      </div>
                      <div className="mat-col">{e.personaAsignada || '—'}</div>
                      <div className="mat-col">{e.ubicacion || '—'}</div>
                      <div className="mat-col">
                        <span className={`mat-badge-estado ${claseEstado(e.estado)}`}>{e.estado}</span>
                      </div>
                      <div className="mat-col mat-col-acciones">
                        <button className="mat-btn-icono" onClick={() => onEdit(e)} title="Editar">
                          <FiEdit2 />
                        </button>
                        <button className="mat-btn-icono mat-btn-icono-danger" onClick={() => pedirBorrado(e.id)} title="Eliminar">
                          <FiTrash2 />
                        </button>
                        <Link to={`/dashboard/equipos/${e.id}/perifericos`} className="mat-btn-icono" title="Ver periféricos">
                          <FiHardDrive />
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Paginación */}
        {filtered.length > itemsPerPage && (
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Anterior
            </button>
            <span>Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Siguiente
            </button>
          </div>
        )}
      </div>

      <Popup
        open={!!borrarId}
        title="Eliminar equipo"
        onClose={() => setBorrarId(null)}
        showConfirm={false}
        footerActions={(
          <button className="boton-aceptar-popup" onClick={confirmarBorrado}>Eliminar</button>
        )}
      >
        <p>¿Seguro que quieres eliminar este equipo? Esta acción no se puede deshacer.</p>
      </Popup>
    </main>
  );
};

export default EquiposManager;
